import { create } from 'zustand';

/**
 * Auth session state — memory-only, on purpose. The actual session lives
 * entirely in the httpOnly cookie the server sets on login (see
 * server/src/controllers/authController.js's setAuthCookie()), which this
 * store can never read directly — that's the whole point of it being
 * httpOnly (see the localStorage -> cookie migration this replaced).
 *
 * That invisibility is exactly what `status` exists to handle: on a fresh
 * page load, a hard refresh, or a new tab, this store has no way to know
 * whether a valid session cookie already exists until it actually asks the
 * server. `status` stays 'loading' until that first check resolves — see
 * App.jsx's bootstrap effect, which calls GET /api/auth/me once on mount
 * and calls setUser()/logout() below with the result. RequireAuth.jsx and
 * RequireAdmin.jsx both wait for 'loading' to clear before deciding whether
 * to redirect, so a perfectly valid session doesn't get bounced to /login
 * just because the cookie hasn't been confirmed yet.
 */
export const useAuthStore = create((set) => ({
  user: null,
  status: 'loading', // 'loading' | 'authenticated' | 'guest'

  // Used both by App.jsx's bootstrap effect (GET /api/auth/me on load) and
  // by any page that re-syncs the user after an unrelated update (e.g.
  // TwoFactorSetup.jsx, Dashboard.jsx's shipping-address save) — passing
  // null is a valid, deliberate way to mark the session as logged-out.
  setUser: (user) => set({ user, status: user ? 'authenticated' : 'guest' }),

  // Called right after a successful login/2FA-completion response, which
  // already includes the user object in its body — no extra round trip
  // needed on the login path itself.
  setSession: (user) => set({ user, status: 'authenticated' }),

  logout: () => set({ user: null, status: 'guest' }),
}));
